/**
 * Fork-specific feature: lets the reading physician favorite individual images
 * (a specific SOP instance / frame) while viewing a study. Favorites are stored
 * in `localStorage` under `FAVORITE_IMAGES_STORAGE_KEY` and are therefore
 * per-browser, not per-user. There is intentionally no server persistence.
 *
 * See "Fork-Specific Changes" in the repo-root CLAUDE.md.
 */

export const FAVORITE_IMAGES_STORAGE_KEY = 'radsync:favorite-images';

export type FavoriteImage = {
  studyInstanceUID: string;
  seriesInstanceUID: string;
  sopInstanceUID: string;
  /** 1-based frame number; 1 for single-frame instances. */
  frameNumber: number;
  /** Denormalized for rendering the favorites modal without re-reading metadata. */
  seriesDescription?: string;
  modality?: string;
  instanceNumber?: number;
  addedAt: number;
};

type Listener = () => void;

const listeners = new Set<Listener>();

/**
 * Cached snapshot so `getFavorites()` returns a stable reference between writes
 * (required by `useSyncExternalStore`). Invalidated on every `persist`.
 */
let cachedFavorites: FavoriteImage[] | null = null;

/** Stable identity of a favorite: study + SOP instance + frame. */
export function getFavoriteKey(
  entry: Pick<FavoriteImage, 'studyInstanceUID' | 'sopInstanceUID' | 'frameNumber'>
): string {
  return `${entry.studyInstanceUID}|${entry.sopInstanceUID}|${entry.frameNumber ?? 1}`;
}

export function getFavorites(): FavoriteImage[] {
  if (cachedFavorites) {
    return cachedFavorites;
  }
  try {
    const raw = window.localStorage.getItem(FAVORITE_IMAGES_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    cachedFavorites = Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn('favoriteImagesStore: unable to read favorites', error);
    cachedFavorites = [];
  }
  return cachedFavorites;
}

function persist(favorites: FavoriteImage[]): void {
  cachedFavorites = favorites;
  try {
    window.localStorage.setItem(FAVORITE_IMAGES_STORAGE_KEY, JSON.stringify(favorites));
  } catch (error) {
    console.warn('favoriteImagesStore: unable to persist favorites', error);
  }
  listeners.forEach(listener => {
    try {
      listener();
    } catch (error) {
      console.warn('favoriteImagesStore: listener threw', error);
    }
  });
}

export function isFavorite(
  entry: Pick<FavoriteImage, 'studyInstanceUID' | 'sopInstanceUID' | 'frameNumber'>
): boolean {
  const key = getFavoriteKey(entry);
  return getFavorites().some(fav => getFavoriteKey(fav) === key);
}

/** True when the given series has at least one favorited image. */
export function seriesHasFavorite(seriesInstanceUID: string): boolean {
  return getFavorites().some(fav => fav.seriesInstanceUID === seriesInstanceUID);
}

export function addFavorite(entry: Omit<FavoriteImage, 'addedAt'>): void {
  const favorites = getFavorites();
  const key = getFavoriteKey(entry);
  if (favorites.some(fav => getFavoriteKey(fav) === key)) {
    return;
  }
  persist([...favorites, { ...entry, frameNumber: entry.frameNumber ?? 1, addedAt: Date.now() }]);
}

export function removeFavorite(
  entry: Pick<FavoriteImage, 'studyInstanceUID' | 'sopInstanceUID' | 'frameNumber'>
): void {
  const key = getFavoriteKey(entry);
  const favorites = getFavorites();
  const next = favorites.filter(fav => getFavoriteKey(fav) !== key);
  if (next.length !== favorites.length) {
    persist(next);
  }
}

/** Adds the entry if missing, removes it if present. Returns the new state. */
export function toggleFavorite(entry: Omit<FavoriteImage, 'addedAt'>): boolean {
  if (isFavorite(entry)) {
    removeFavorite(entry);
    return false;
  }
  addFavorite(entry);
  return true;
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
